import React, { useEffect, useState } from "react";
import { cn } from "./cn";
import { SearchIcon } from "./icons";

export type ScrollingSearchInputProps = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hints: string[];
  prefix?: string;
  className?: string;
  inputClassName?: string;
  cycleMs?: number;
};

export function ScrollingSearchInput({
  value,
  onChange,
  hints,
  prefix = "Search by ",
  className,
  inputClassName,
  cycleMs = 2500,
}: ScrollingSearchInputProps) {
  const [focused, setFocused] = useState(false);
  const [hintIndex, setHintIndex] = useState(0);
  const showOverlay = !value && !focused && hints.length > 0;

  useEffect(() => {
    if (!showOverlay || hints.length <= 1 || typeof window === "undefined") return;
    const id = window.setInterval(() => {
      setHintIndex((i) => (i + 1) % hints.length);
    }, cycleMs);
    return () => window.clearInterval(id);
  }, [showOverlay, hints.length, cycleMs]);

  const activeHint = hints[hintIndex] ?? hints[0] ?? "";

  return (
    <div className={cn("relative", className)}>
      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
      <input
        type="text"
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label={prefix + activeHint}
        className={cn(
          "flex h-11 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm shadow-xs transition-colors",
          "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          inputClassName,
        )}
      />
      {showOverlay ? (
        <div
          className="absolute inset-y-0 left-9 right-3 flex items-center pointer-events-none overflow-hidden text-sm"
          aria-hidden
        >
          <span className="shrink-0 text-muted-foreground">{prefix}</span>
          <span className="relative h-5 flex-1 min-w-0 ml-1 overflow-hidden">
            {hints.map((hint, i) => (
              <span
                key={hint}
                className={cn(
                  "absolute inset-0 truncate text-muted-foreground transition-all duration-500 ease-in-out",
                  i === hintIndex
                    ? "opacity-100 translate-y-0"
                    : i < hintIndex || (hintIndex === 0 && i === hints.length - 1)
                      ? "opacity-0 -translate-y-full"
                      : "opacity-0 translate-y-full",
                )}
              >
                {hint}
              </span>
            ))}
          </span>
        </div>
      ) : null}
    </div>
  );
}
